// deviceOsTargeting.js — AS-116
// Utilities for building PAC payload and parsing AdGroup response for
// Device / OS / Browser targeting.

export const DEVICE_ALL_VALUES = ['pc', 'tablet', 'phone', 'tv'];
export const OS_ALL_VALUES     = ['ios', 'android', 'windows', 'linux', 'macos'];
export const BROWSER_ALL_VALUES = ['chrome', 'firefox', 'samsung', 'qq', 'ie', 'android', 'opera', 'safari'];

/**
 * Default state: all = true (no restriction).
 */
export function makeDefaultState() {
  return { all: true, mode: 'include', selected: [] };
}

/**
 * Build the PAC-compatible fields for one targeting dimension.
 * Returns { all, mode, selected } object to be sent to the backend.
 */
export function buildDimensionPayload(state) {
  return {
    all: state.all,
    mode: state.mode || 'include',
    selected: state.all ? [] : (state.selected || []),
  };
}

/**
 * Parse device/os/browser state from an AdGroup response.
 * Handles both v3 schema (top-level fields present) and v2 fallback.
 *
 * @param {object} data           — raw AdGroup data (top-level fields)
 * @param {object} ts2            — parsed targetingSettings_v2 object
 * @param {string} dimension      — one of 'device', 'os', 'browser'
 * @returns {{ all, mode, selected }}
 */
export function parseDimensionFromAdGroup(data, ts2, dimension) {
  if (dimension === 'device') {
    if (data.device_all !== undefined) {
      // v3 schema
      return {
        all:      !!data.device_all,
        mode:     data.device_mode || 'include',
        selected: data.device || [],
      };
    }
    // v2 fallback: device_type_v2 { allow_all, whitelist }
    const v2 = ts2 && ts2.device_type_v2;
    if (v2 && v2.allow_all === false) {
      return { all: false, mode: 'include', selected: v2.whitelist || [] };
    }
    return makeDefaultState();
  }

  if (dimension === 'os') {
    if (data.mobile_os_all !== undefined) {
      // v3 schema
      return {
        all:      !!data.mobile_os_all,
        mode:     data.mobile_os_mode || 'include',
        selected: data.mobile_os || [],
      };
    }
    // v2 fallback: os_acl { whitelist } — no allow_all, no blacklist
    const osAcl = ts2 && ts2.os_acl;
    if (osAcl && (osAcl.whitelist || []).length > 0) {
      return { all: false, mode: 'include', selected: osAcl.whitelist };
    }
    return makeDefaultState();
  }

  if (dimension === 'browser') {
    if (data.browser_all !== undefined) {
      // v3 schema
      return {
        all:      !!data.browser_all,
        mode:     data.browser_mode || 'include',
        selected: data.browser || [],
      };
    }
    // v2: browser targeting didn't exist → default all
    return makeDefaultState();
  }

  return makeDefaultState();
}

/**
 * Validate device/os/browser states before submit.
 * Returns an error message string or null if valid.
 */
export function validateDeviceOsStates(deviceState, osState, browserState) {
  if (!deviceState.all && deviceState.selected.length === 0) {
    return '請選擇至少一個裝置，或勾選「全部裝置」';
  }
  if (!osState.all && osState.selected.length === 0) {
    return '請選擇至少一個作業系統，或勾選「全部作業系統」';
  }
  if (!browserState.all && browserState.selected.length === 0) {
    return '請選擇至少一個瀏覽器，或勾選「全部瀏覽器」';
  }
  return null;
}
